/**
 * The ONE list of public paths that have a Markdown twin (`<path>.md`, served
 * by `app/api/markdown` via the proxy rewrite). LEAF MODULE on purpose: both
 * `lib/site/config.tsx` (which advertises `rel=alternate text/markdown`) and
 * `lib/site/seo/markdown-content.ts` (which serves the twins) import it, and
 * the two must never drift — advertising a twin that 404s poisons crawlers,
 * and serving a twin nothing advertises wastes it.
 */

/** Static public marketing paths that always have a Markdown twin. */
export const PUBLIC_MARKDOWN_PATHS = [
  '/',
  '/pricing',
  '/enterprise',
  '/mcp',
  '/marketers',
  '/startups',
  '/agencies',
  '/ecommerce',
  '/developers',
  '/support',
  '/email-audit',
  '/university',
  '/explore',
  '/browse/templates',
  '/browse/flows',
  '/browse/integrations',
  '/blog',
  '/customers',
  '/compare',
  '/sign-up',
  '/sign-in',
] as const

// These static files share /blog with [slug]. Keep exact paths so a missing
// image-shaped article still gets a real 404. seo-editorial.test checks this
// list against public/blog; no image bytes belong in the proxy bundle.
export const PUBLIC_BLOG_IMAGE_PATHS = [
  '/blog/brew-mcp-architecture.png',
  '/blog/brew-mcp-compose.png',
  '/blog/cory-nelson-call.png',
  '/blog/email-design-md-editor.png',
  '/blog/email-design-md-extraction.png',
  '/blog/email-design-md-firecrawl-emails.png',
  '/blog/email-design-md-firecrawl-spec.png',
  '/blog/email-design-md-ingest.png',
  '/blog/german-car-door.jpeg',
  '/blog/launch-war-room.png',
  '/blog/product-hunt-impact-chart.png',
  '/blog/product-hunt-launch-page.png',
  '/blog/product-hunt-monthly-leaderboard.png',
] as const

const publicBlogImages: ReadonlySet<string> = new Set(PUBLIC_BLOG_IMAGE_PATHS)

export function isPublicBlogImagePath(pathname: string): boolean {
  return publicBlogImages.has(pathname)
}

/**
 * Paths that already speak a non-HTML machine format. The proxy must not
 * rewrite them to `/api/markdown` when `Accept: text/markdown` is present —
 * an agent asking for `/openapi.json` should get the spec, not a 404 twin.
 */
/**
 * Next.js App Router flight requests. Rewriting these to markdown
 * would break client navigations and Instant Prefetch.
 */
export function isRscNavigationRequest(headers: {
  get(name: string): string | null
  has(name: string): boolean
}): boolean {
  if (
    headers.has('RSC') ||
    headers.has('Next-Router-State-Tree') ||
    headers.has('Next-Router-Prefetch')
  ) {
    return true
  }
  const accept = headers.get('accept') ?? ''
  return accept.includes('text/x-component')
}

export function isMarkdownNegotiationExemptPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/{2,}/g, '/').toLowerCase()
  return (
    isPublicBlogImagePath(pathname) ||
    normalized === '/og' ||
    /\/opengraph-image(?:-[a-z0-9]{1,6})?(?:\/[a-z0-9_-]{1,32})?$/.test(
      normalized
    ) ||
    normalized === '/openapi.json' ||
    normalized === '/openapi.yaml' ||
    normalized === '/sitemap.xml' ||
    normalized === '/robots.txt' ||
    normalized === '/llms.txt' ||
    normalized === '/llms-full.txt' ||
    normalized === '/mcp.json' ||
    normalized === '/.well-known/mcp.json' ||
    normalized === '/.well-known/mcp/server.json' ||
    normalized.startsWith('/openapi/')
  )
}

/**
 * Dynamic route families whose members resolve a Markdown twin at request
 * time (`resolvePublicMarkdown` handles unknown ids per family).
 */
const PUBLIC_MARKDOWN_PATH_PREFIXES = [
  '/blog/',
  '/compare/',
  '/customers/',
  '/browse/integrations/',
  '/browse/flows/',
  '/browse/templates/email/',
] as const

/**
 * A template's readable URL, `/templates/{brand}/{title}` (the `email` and
 * `flows` segments belong to other routes; see `lib/templates/public-path.ts`).
 */
const TEMPLATE_DETAIL_PATH_RE =
  /^\/templates\/(?!(?:email|flows)\/)[a-z0-9]+(?:-[a-z0-9]+)*\/[a-z0-9]+(?:-[a-z0-9]+)*$/

const staticPaths: ReadonlySet<string> = new Set(PUBLIC_MARKDOWN_PATHS)

/** Does this route path have a Markdown twin to advertise? */
export function hasPublicMarkdownTwin(path: string): boolean {
  return (
    !isPublicBlogImagePath(path) &&
    (staticPaths.has(path) ||
      TEMPLATE_DETAIL_PATH_RE.test(path) ||
      PUBLIC_MARKDOWN_PATH_PREFIXES.some(
        (prefix) => path.startsWith(prefix) && path.length > prefix.length
      ))
  )
}

/**
 * The path a request to `/api/markdown` is actually asking for.
 *
 * Under a middleware rewrite, Next (16.3) surfaces the ORIGINAL client URL on
 * both `request.url` and `request.nextUrl` — the rewrite target's `?path=`
 * query never reaches the handler, so reading only the query made every
 * rewritten `.md` twin fall back to `/` (the home page). Verified against a
 * live dev server. Resolution order:
 *
 *  1. an explicit `?path=` query (direct invocation, or a host that does
 *     surface the rewrite URL)
 *  2. an original `<base>.md` pathname (the proxy's `.md` rewrite lane)
 *  3. any other original pathname (the `Accept: text/markdown` lane)
 */
export function resolveRequestedMarkdownPath(args: {
  pathname: string
  pathQuery: string | null
}): string {
  if (args.pathQuery) {
    return args.pathQuery
  }
  if (args.pathname.endsWith('.md')) {
    const base = args.pathname.slice(0, -3)
    return base === '' ? '/' : base
  }
  if (args.pathname !== '/api/markdown') {
    return args.pathname
  }
  return '/'
}
